import { FC, useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, Star } from 'lucide-react';
import type { Note, CourseReview } from '@teachur/http-client';

interface NotesHistoryProps {
  notes: Note[];
  reviews?: CourseReview[];
}

interface HistoryItemProps {
  note: Note;
  review?: CourseReview;
}

const HistoryItem: FC<HistoryItemProps> = ({ note, review }) => {
  const [expanded, setExpanded] = useState(false);
  const hasGrades = review?.grades && review.grades.length > 0;

  return (
    <div className="border border-(--neutral-black-NB300) rounded-lg! overflow-hidden">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex justify-between items-center bg-(--neutral-white-NW900) px-4 py-3 cursor-pointer text-left"
      >
        <div className="flex flex-col gap-0.5">
          {note.approver && (
            <p className="font-semibold text-(--neutral-black-NB800)! [font-size:var(--fs-l5)]! m-0!">
              {note.approver.firstName} {note.approver.lastName}
            </p>
          )}
          {note.text?.trim() && (
            <p className="text-(--neutral-black-NB700)! [font-size:var(--fs-l5)]! m-0! line-clamp-1">
              {note.text}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {note.approved ? (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md! bg-(--success-S200)">
              <CheckCircle2 className="w-3.5 h-3.5 text-(--success-S700)" />
              <span className="font-medium [font-size:var(--fs-l5)]! text-(--success-S700) m-0!">
                Approved
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md! bg-(--danger-D200)">
              <XCircle className="w-3.5 h-3.5 text-(--danger-D700)" />
              <span className="font-medium [font-size:var(--fs-l5)]! text-(--danger-D700) m-0!">
                Not Yet
              </span>
            </div>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-(--neutral-black-NB500)" />
          ) : (
            <ChevronDown className="w-4 h-4 text-(--neutral-black-NB500)" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 flex flex-col gap-3 bg-white border-t border-(--neutral-black-NB300)">
          {hasGrades && (
            <div className="flex flex-col gap-2">
              <p className="font-semibold text-(--neutral-black-NB800)! [font-size:var(--fs-l5)]! m-0!">
                Goals
              </p>
              {review!.grades!.map((grade) => (
                <div
                  key={grade.id}
                  className="flex flex-col gap-1 rounded-[8px] border border-(--neutral-white-NW800) p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-black-NB900)! leading-snug">
                      {grade.goal?.text ?? `Goal #${grade.goalId}`}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${i < grade.grade ? 'fill-(--primary-O1000) text-(--primary-O1000)' : 'text-(--neutral-black-NB300)'}`}
                        />
                      ))}
                    </div>
                  </div>
                  {grade.note && (
                    <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-black-NB600)! mt-1!">
                      {grade.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {note.text?.trim() && (
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-(--neutral-black-NB800)! [font-size:var(--fs-l5)]! m-0!">
                General Note
              </p>
              <p className="text-(--neutral-black-NB700)! [font-size:var(--fs-l5)]! m-0!">
                {note.text}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const NotesHistory: FC<NotesHistoryProps> = ({ notes, reviews = [] }) => {
  if (!notes || notes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 pt-3 border-t border-(--neutral-black-NB300)">
      <h4 className="font-bold text-(--neutral-black-NB800)! [font-size:var(--fs-l3)]! m-0!">
        History
      </h4>
      <div className="flex flex-col gap-2">
        {notes.map((note, index) => {
          const review = reviews.find((r) => r.scheduledCallId === note.scheduledCallId);
          return <HistoryItem key={note.id ?? index} note={note} review={review} />;
        })}
      </div>
    </div>
  );
};
